import { notEmpty } from "@orb-labs/types";
/**
 * Latest unix-second timestamp across probability chart series (for aligned live dots).
 * Each series must be sorted by time ascending (last element = latest point).
 */
export function resolveAlignedLineEndTime(allLineData) {
    let maxSec = 0;
    for (const lineData of allLineData) {
        const last = lineData[lineData.length - 1];
        if (!last)
            continue;
        const t = last.time;
        if (typeof t !== "number")
            continue;
        if (t > maxSec)
            maxSec = t;
    }
    return maxSec > 0 ? maxSec : undefined;
}
/**
 * Normalizes a raw prediction probability to the 0–100 percentage scale the
 * charts render on. Probabilities arrive either as a float in [0, 1]
 * (`pricePerOutcome`) or BigInt micro-units (÷ 1e6) when sourced from the
 * scaled price path. Returns `undefined` for values that are non-finite,
 * negative, or still out of range after de-scaling — so both the historical
 * mapper and the live tick handler drop the same malformed inputs identically
 * instead of plotting garbage. Live and historical paths MUST share this so a
 * streamed tick lands on the same scale as the bars it extends.
 */
export function rawProbabilityToPercent(raw) {
    if (!Number.isFinite(raw) || raw < 0)
        return undefined;
    let val = raw;
    if (val > 1)
        val /= 1e6;
    if (val > 1)
        return undefined;
    return val * 100;
}
/**
 * Maps raw price time series points to sorted line data for probability charts (values as 0–100).
 * Deduplicates by second; values greater than 1 are treated as micro-units (÷ 1e6) then × 100.
 */
export function pointsToProbabilityLineSeries(points) {
    const data = [];
    const seen = new Set();
    for (const pt of points) {
        if (!notEmpty(pt.timestamp) || !notEmpty(pt.value))
            continue;
        const ts = Math.floor((pt.timestamp instanceof Date ? pt.timestamp.getTime() : new Date(pt.timestamp).getTime()) / 1000);
        if (seen.has(ts))
            continue;
        const value = rawProbabilityToPercent(Number(pt.value));
        if (!notEmpty(value))
            continue;
        seen.add(ts);
        data.push({ time: ts, value });
    }
    return data.sort((a, b) => a.time - b.time);
}
export function getChartPointsFromMap(outcome, chartDataMap) {
    const tokenId = outcome.externalTokenId.trim();
    if (!tokenId)
        return [];
    const series = chartDataMap.get(tokenId);
    return series?.price ?? [];
}
